import React, { useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, Stack, Tooltip } from "@mui/material";
import "quill/dist/quill.snow.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faHeading,
  faItalic,
  faListOl,
  faListUl,
  faPenToSquare,
  faTimes,
  faUnderline,
  faSave,
} from "@fortawesome/free-solid-svg-icons";

import Quill from "quill";

// Editor is an uncontrolled React component
const QuillEditor = ({ initialHtml, toolbarId, onSave, disabled }) => {
  const quillRef = useRef();
  const containerRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [savedContent, setSavedContent] = useState(initialHtml);

  // Init Quill
  useEffect(() => {
    if (!containerRef.current) return;

    quillRef.current = new Quill(containerRef.current, {
      theme: "snow",
      readOnly: true,
      formats: ["header", "bold", "italic", "underline", "list"],
      modules: {
        toolbar: `#${toolbarId}`,
      },
    });

    quillRef.current.clipboard.dangerouslyPasteHTML(initialHtml);
  }, [initialHtml, toolbarId]);

  // Update content when savedContent changes
  useEffect(() => {
    if (!quillRef.current || isEditing) return;
    quillRef.current.clipboard.dangerouslyPasteHTML(savedContent);
    quillRef.current.enable(false);
  }, [savedContent, isEditing]);

  const toggleEdit = () => {
    if (quillRef.current) {
      quillRef.current.enable(true);
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    if (!quillRef.current) return;

    const html = quillRef.current.root.innerHTML;

    if (html !== savedContent) {
      setSavedContent(html);
      onSave?.(html);
    }

    setIsEditing(false);
    quillRef.current.enable(false);
  };

  const handleCancel = () => {
    if (!quillRef.current) return;
    quillRef.current.clipboard.dangerouslyPasteHTML(savedContent);
    quillRef.current.enable(false);
    setIsEditing(false);
  };

  return (
    <Box
      sx={{
        "& .ql-editor": { padding: 0 },
        "& .ql-toolbar.ql-snow": {
          borderColor: "divider",
          backgroundColor: "background.default",
          marginBottom: 2,
          mx: "-2.5rem",
          mt: `calc(-1.2rem - 1px)`,
        },
        "& .ql-container.ql-snow": {
          border: "none",
        },
        position: "relative",
      }}
    >
      {!disabled && !isEditing && (
        <Box sx={{ position: "absolute", top: "0", right: "-1rem", zIndex: 2 }}>
          <Tooltip title="Edit">
            <IconButton onClick={toggleEdit} sx={{ color: "primary.main" }}>
              <FontAwesomeIcon icon={faPenToSquare} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Box
        sx={{
          visibility: isEditing ? "visible" : "hidden",
          height: isEditing ? "auto" : 0,
          pt: isEditing ? 0 : 0.5,
        }}
      >
        <Stack id={toolbarId} direction="row" spacing={1} flexWrap="wrap">
          <Tooltip title="Bold">
            <IconButton className="ql-bold">
              <FontAwesomeIcon icon={faBold} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton className="ql-italic">
              <FontAwesomeIcon icon={faItalic} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underline">
            <IconButton className="ql-underline">
              <FontAwesomeIcon icon={faUnderline} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Heading 1">
            <IconButton className="ql-header" value="1">
              <FontAwesomeIcon icon={faHeading} size="sm" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Heading 2">
            <IconButton className="ql-header" value="2">
              <FontAwesomeIcon icon={faHeading} size="xs" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bullet List">
            <IconButton className="ql-list" value="bullet">
              <FontAwesomeIcon icon={faListUl} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numbered List">
            <IconButton className="ql-list" value="ordered">
              <FontAwesomeIcon icon={faListOl} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box ref={containerRef} />

      {isEditing && (
        <Box mt={2} display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<FontAwesomeIcon icon={faTimes} />}
          >
            Zrušit
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<FontAwesomeIcon icon={faSave} />}
          >
            Uložit
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default QuillEditor;
